import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Clock, CheckCircle, XCircle, FileText, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function MealReservationValidation({ user }) {
  const [loading, setLoading] = useState(false);
  const [pendingReservations, setPendingReservations] = useState([]);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    fetchPendingReservations();
  }, []);

  const fetchPendingReservations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/meal-reservations/pending`);
      setPendingReservations(response.data);
    } catch (error) {
      console.error('Error fetching pending reservations:', error);
      toast.error('Erreur lors du chargement des réservations');
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async (reservationId) => {
    try {
      const response = await axios.post(`${API}/meal-reservations/${reservationId}/validate`);
      toast.success(response.data.message);
      fetchPendingReservations();
    } catch (error) {
      console.error('Error validating reservation:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    }
  };

  const handleReject = async () => {
    if (!selectedReservation) return;

    try {
      await axios.post(
        `${API}/meal-reservations/${selectedReservation.id}/reject`,
        null,
        { params: { reason: rejectReason } }
      );
      toast.success('Réservation rejetée avec succès');
      setRejectDialogOpen(false);
      setRejectReason('');
      setSelectedReservation(null);
      fetchPendingReservations();
    } catch (error) {
      console.error('Error rejecting reservation:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du rejet');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date inconnue';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (error) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Clock className="w-5 h-5 mr-2 text-yellow-600" />
            Réservations en attente de validation
          </CardTitle>
          <CardDescription>
            Validez ou rejetez les demandes de réservation de repas
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pendingReservations.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">
                Aucune réservation en attente
              </p>
              <p className="text-gray-400 text-sm mt-2">
                Toutes les demandes ont été traitées !
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingReservations.map((reservation) => (
                <Card key={reservation.id} className="border-l-4 border-l-yellow-500">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-3">
                          <h3 className="text-lg font-semibold">{reservation.menu_title}</h3>
                          <Badge className="bg-yellow-100 text-yellow-800">
                            <Clock className="w-3 h-3 mr-1" />
                            En attente
                          </Badge>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                          <div>
                            <p className="text-sm text-gray-500">Date du repas</p>
                            <p className="font-medium">{formatDate(reservation.menu_date)}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Élève</p>
                            <p className="font-medium">{reservation.student_name}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Parent</p>
                            <p className="font-medium">{reservation.parent_name}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Quantité</p>
                            <p className="font-medium">{reservation.quantity} repas</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Prix unitaire</p>
                            <p className="font-medium">{reservation.price_per_meal?.toLocaleString()} GNF</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Montant total</p>
                            <p className="font-bold text-green-600 text-lg">
                              {reservation.total_amount?.toLocaleString()} GNF
                            </p>
                          </div>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
                          <p className="text-xs text-blue-800">
                            <AlertCircle className="w-4 h-4 inline mr-1" />
                            <strong>Action :</strong> En validant, une facture sera automatiquement créée et envoyée au parent.
                          </p>
                        </div>

                        <p className="text-xs text-gray-400">
                          Demandé le {formatDate(reservation.requested_at)}
                        </p>
                      </div>

                      <div className="flex flex-col space-y-2 ml-4">
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleValidate(reservation.id)}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Valider
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            setSelectedReservation(reservation);
                            setRejectDialogOpen(true);
                          }}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Rejeter
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog de rejet */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejeter la réservation</DialogTitle>
            <DialogDescription>
              Indiquez la raison du rejet (facultatif). Le parent sera notifié.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="reason">Raison du rejet</Label>
              <Textarea
                id="reason"
                placeholder="Ex: Menu complet, date incorrecte, etc."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={4}
              />
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setRejectDialogOpen(false);
                  setRejectReason('');
                  setSelectedReservation(null);
                }}
              >
                Annuler
              </Button>
              <Button
                variant="destructive"
                onClick={handleReject}
              >
                <XCircle className="w-4 h-4 mr-1" />
                Confirmer le rejet
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default MealReservationValidation;
